import {
  MemoizedSelector,
  createSelector,
  createSelectorFactory,
  defaultMemoize,
} from '@ngrx/store';

import { OfferBrief, TalentPoolBrief } from '@recruitee/http/types';
import { createEntityCollectionStore, memoizeSelector } from '@recruitee/ngrx';

import { EntityStoreKey, OfferScope } from '../../types';

const { actions, namespace, selectors, reducer } = createEntityCollectionStore<OfferBrief>()(
  EntityStoreKey.TalentPoolsBrief,
  {
    customActions: adapter => ({
      fetchCollection: (state, payload?: { scope: OfferScope }) =>
        adapter.markAsPending(state, { scope: payload?.scope || null, entities: [] }),
      fetchActiveCollection: state =>
        adapter.markAsPending(state, { scope: OfferScope.Active, entities: [] }),
      fetchArchivedCollection: state =>
        adapter.markAsPending(state, { scope: OfferScope.Archived, entities: [] }),
      upsertOne: (state, payload: { data: OfferBrief; scope: OfferScope }) =>
        adapter.upsertOne(state, payload.data, { scope: payload.scope }),
      upsertMany: (state, payload: { data: OfferBrief[]; scope: OfferScope }) =>
        adapter.upsertMany(state, payload.data, { scope: payload.scope }),
    }),
  },
);

// entities
const active = createSelector(selectors.entities({ scope: OfferScope.Active }), state => state);
const archived = createSelector(selectors.entities({ scope: OfferScope.Archived }), state => state);
const all = createSelector(active, archived, (active, archived) => [...active, ...archived]);

// meta
const activeScopeIsLoaded = createSelector(
  selectors.isLoaded({ scope: OfferScope.Active }),
  state => state,
);
const archivedScopeIsLoaded = createSelector(
  selectors.isLoaded({ scope: OfferScope.Archived }),
  state => state,
);

const byIds = memoizeSelector(
  (offerIds: number[]): MemoizedSelector<any, TalentPoolBrief[]> =>
    createSelectorFactory(defaultMemoize)(
      ...(offerIds || []).map(offerId => selectors.byId(offerId)),
      (...result: TalentPoolBrief[]) => result,
    ),
);

const customSelectors = {
  all,
  active,
  archived,
  activeScopeIsLoaded,
  archivedScopeIsLoaded,
  canAssignTo: active,
  byIds,
};

const talentPoolsBriefSelectors = {
  ...selectors,
  ...customSelectors,
};

export {
  actions as TalentPoolsBriefActions,
  namespace as TalentPoolsBriefNamespace,
  talentPoolsBriefSelectors as TalentPoolsBriefSelectors,
  reducer as TalentPoolsBriefReducer,
};
